import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormAbstact } from 'app/views/pages/abstract/form-abstract';
import { EmployeeFeedbackService } from 'services/feedback/employeeFeedback.service';
import { EmployeeModel } from 'services/models/employee.model';
import { EmployeeFeedbackModel } from 'services/models/employeeFeedback.model';
import { EmployeeFeedbackVisibilityMode } from 'services/models/employeeFeedbackSettings.model';
import { ToastService } from '../../../../../../@core/components/toasts/toasts.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDialogComponent } from 'app/layout/components/confirm-dialog/confirm-dialog.component';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

export enum FeedbackFormMode {
  Create = 'create',
  Edit = 'edit',
  View = 'view',
}

@Component({
  selector: 'app-feedback-form',
  templateUrl: './feedback-form.component.html',
  styleUrls: ['./feedback-form.component.scss'],
})
export class FeedbackFormComponent
  extends FormAbstact<EmployeeFeedbackModel, EmployeeFeedbackService>
  implements OnInit, OnChanges, OnDestroy
{
  readonly FeedbackFormMode = FeedbackFormMode;

  @Input() mode: FeedbackFormMode = FeedbackFormMode.View;
  @Input() requestFeedback: any;
  @Input() feedbackView: any;
  @Input() employee?: EmployeeModel;
  @Output() onCancel = new EventEmitter();
  @Input() organicFeedback: boolean = false;
  @Input() HomeFeedback: boolean = false;
  @Input() data: any;
  currentEmployee?: EmployeeModel;
  feedbackVisibilityMode: EmployeeFeedbackVisibilityMode = EmployeeFeedbackVisibilityMode.Hidden;
  readonly visibilityMode = EmployeeFeedbackVisibilityMode;

  public checkingField: Record<FeedbackTextareaField, boolean> = {
    startDoing: false,
    keepDoing: false,
    stopDoing: false,
  };

  public checkingMessage: Record<FeedbackTextareaField, string> = {
    startDoing: '',
    keepDoing: '',
    stopDoing: '',
  };
  public readonly checkingText = 'Aguarde um momento...';
  public suggestionMessage: Record<FeedbackTextareaField, string> = {
    startDoing: '',
    keepDoing: '',
    stopDoing: '',
  };

  public suggestionApplied: Record<FeedbackTextareaField, boolean> = {
    startDoing: false,
    keepDoing: false,
    stopDoing: false,
  };

  private previousMessageBeforeSuggestion: Record<FeedbackTextareaField, string> = {
    startDoing: '',
    keepDoing: '',
    stopDoing: '',
  };

  private appliedSuggestionMessage: Record<FeedbackTextareaField, string> = {
    startDoing: '',
    keepDoing: '',
    stopDoing: '',
  };

  private readonly suggestionAppliedMessageDuration = 15000;
  private suggestionAppliedTimeoutByField: Partial<Record<FeedbackTextareaField, number>> = {};
  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: EmployeeFeedbackService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private toasterService: ToastService,
    private _modalService: NgbModal,
    @Optional() private activeModal: NgbActiveModal
  ) {
    super(service, activeRoute, router, toasterService);
  }

  ngOnInit(): void {
    this._syncCurrentEmployee();
    this.initializeForm();
    this.loadFeedbackVisibilitySettings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['requestFeedback'] || changes['employee'] || changes['mode']) {
      this._syncCurrentEmployee();
      if (this.form && (changes['data'] || changes['requestFeedback'] || changes['employee'])) {
        this.initializeForm();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    (['startDoing', 'keepDoing', 'stopDoing'] as FeedbackTextareaField[]).forEach(field => {
      this.clearSuggestionAppliedTimeout(field);
    });
  }

  private _syncCurrentEmployee(): void {
    this.currentEmployee =
      this.employee ||
      this.data?.employee ||
      this.requestFeedback?.employee ||
      (this.requestFeedback as EmployeeModel) ||
      null;
  }

  get drawerTitle(): string {
    return this.mode === FeedbackFormMode.Create ? 'Feedbacks' : 'Detalhes do feedback';
  }

  handleClose(): void {
    this.handleCancel(false);
  }

  public initializeForm(): void {
    const employeeId =
      this.requestFeedback?.employeeId || this.data?.employee?.id || this.employee?.id || null;
    const textareaValidators = [Validators.maxLength(1000), nonWhitespaceValidator];

    this.form = this.formBuilder.group({
      employeeId: [employeeId, Validators.required],
      competenceId: [
        this.requestFeedback?.competenceId || '6ba7b810-9dad-11d1-80b4-00c04fd430c8',
        Validators.required,
      ],
      competenceTitle: [
        this.requestFeedback?.competenceTitle || '00000000-0000-0000-0000-000000000000',
        Validators.required,
      ],
      startDoing: ['', textareaValidators],
      keepDoing: ['', textareaValidators],
      stopDoing: ['', textareaValidators],
      showToEmployee: [false],
    });

    const control = this.form.get('showToEmployee');
    control?.setValue(false, { emitEvent: false });
    this.applyVisibilityModeToForm();

    this.resetSuggestionStates();
  }

  public handleCancel(requestLoadFeedbacks = false): void {
    if (this.activeModal) {
      this.activeModal.dismiss(requestLoadFeedbacks);
    } else {
      this.onCancel.emit(requestLoadFeedbacks);
    }
  }

  public handleSave(): void {
    if (!this.canSubmit) {
      this.form.markAllAsTouched();
      this.toasterService.showError(
        'Preencha pelo menos um dos campos do feedback antes de enviar.'
      );
      return;
    }

    const feedback: EmployeeFeedbackModel = this.form.getRawValue();

    feedback.startDoing = feedback.startDoing?.trim();
    feedback.keepDoing = feedback.keepDoing?.trim();
    feedback.stopDoing = feedback.stopDoing?.trim();
    feedback.showToEmployee = !!feedback.showToEmployee;

    const modalRef = this._modalService.open(ConfirmDialogComponent, {
      windowClass: 'confirm-dialog-modal',
      backdropClass: 'confirm-dialog-backdrop',
    });
    modalRef.componentInstance.message = feedback.showToEmployee
      ? `Deseja realmente enviar o Feedback para o colaborador ${this.currentEmployee.name} ? (Ele receberá uma e-mail de notificação)`
      : `Deseja realmente registrar o Feedback para o colaborador ${this.currentEmployee.name} ?`;
    modalRef.componentInstance.focusConfirm = true;
    modalRef.result.then(confirmed => {
      if (confirmed) {
        this.service
          .sendFeedback(feedback)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.handleCancel(true);
              this.toasterService.showSuccess('Feedback enviado com sucesso!');
            },
            error: () => {},
          });
      }
    });
  }

  public async handleTextareaBlur(field: FeedbackTextareaField): Promise<void> {
    const control = this.form?.get(field);
    if (!control) return;

    if (this.suggestionApplied[field]) {
      return;
    }

    const value: string = control.value ?? '';
    if (!value.trim()) {
      this.resetCheckingState(field);
      this.clearSuggestion(field);
      return;
    }

    if (this.checkingField[field]) {
      return;
    }

    this.checkingField[field] = true;
    this.checkingMessage[field] = this.checkingText;

    this.service
      .checkMessageContent(value)
      .pipe(
        finalize(() => {
          this.checkingField[field] = false;
          this.checkingMessage[field] = '';
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (checkedMessage: string) => {
          if (typeof checkedMessage !== 'string') {
            return;
          }

          if (!checkedMessage.trim()) {
            return;
          }

          const normalizedChecked = this.normalizeMessage(checkedMessage);
          const normalizedOriginal = this.normalizeMessage(value);

          if (!normalizedChecked || normalizedChecked === normalizedOriginal) {
            this.clearSuggestion(field);
            return;
          }

          this.suggestionMessage[field] = checkedMessage;
          this.suggestionApplied[field] = false;
          this.previousMessageBeforeSuggestion[field] = '';
          this.clearSuggestionAppliedTimeout(field);
        },
        error: () => {
          this.resetCheckingState(field);
        },
      });
  }

  public applySuggestion(field: FeedbackTextareaField): void {
    const control = this.form?.get(field);
    const suggestion = this.suggestionMessage[field];

    if (!control || !suggestion?.trim()) {
      return;
    }

    this.previousMessageBeforeSuggestion[field] = control.value ?? '';
    this.appliedSuggestionMessage[field] = suggestion;
    control.setValue(suggestion);
    this.clearSuggestion(field);
    this.suggestionApplied[field] = true;

    this.clearSuggestionAppliedTimeout(field);
    this.suggestionAppliedTimeoutByField[field] = window.setTimeout(() => {
      this.suggestionApplied[field] = false;
      this.previousMessageBeforeSuggestion[field] = '';
      this.clearSuggestionAppliedTimeout(field);
    }, this.suggestionAppliedMessageDuration);
  }

  public undoSuggestion(field: FeedbackTextareaField): void {
    const control = this.form?.get(field);
    if (!control) {
      return;
    }

    control.setValue(this.previousMessageBeforeSuggestion[field] ?? '');
    this.suggestionMessage[field] = this.appliedSuggestionMessage[field] ?? '';
    this.suggestionApplied[field] = false;
    this.previousMessageBeforeSuggestion[field] = '';
    this.appliedSuggestionMessage[field] = '';
    this.clearSuggestionAppliedTimeout(field);
  }

  private resetCheckingState(field: FeedbackTextareaField): void {
    this.checkingField[field] = false;
    this.checkingMessage[field] = '';
  }

  private clearSuggestion(field: FeedbackTextareaField): void {
    this.suggestionMessage[field] = '';
  }

  private clearSuggestionAppliedTimeout(field: FeedbackTextareaField): void {
    const timeoutId = this.suggestionAppliedTimeoutByField[field];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      delete this.suggestionAppliedTimeoutByField[field];
    }
  }

  private resetSuggestionStates(): void {
    (['startDoing', 'keepDoing', 'stopDoing'] as FeedbackTextareaField[]).forEach(field => {
      this.clearSuggestion(field);
      this.suggestionApplied[field] = false;
      this.previousMessageBeforeSuggestion[field] = '';
      this.appliedSuggestionMessage[field] = '';
      this.clearSuggestionAppliedTimeout(field);
    });
  }

  private normalizeMessage(message: string): string {
    return (message ?? '').replace(/\s+/g, ' ').trim();
  }

  private loadFeedbackVisibilitySettings(): void {
    this.service
      .getVisibilitySettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.feedbackVisibilityMode = settings?.mode ?? EmployeeFeedbackVisibilityMode.Hidden;
          this.applyVisibilityModeToForm();
        },
        error: () => {
          this.feedbackVisibilityMode = EmployeeFeedbackVisibilityMode.Hidden;
          this.applyVisibilityModeToForm();
        },
      });
  }

  private applyVisibilityModeToForm(): void {
    const control = this.form?.get('showToEmployee');
    if (!control) {
      return;
    }

    switch (this.feedbackVisibilityMode) {
      case EmployeeFeedbackVisibilityMode.AlwaysVisible:
        control.setValue(true, { emitEvent: false });
        control.disable({ emitEvent: false });
        break;
      case EmployeeFeedbackVisibilityMode.ManagerDecides:
        control.enable({ emitEvent: false });
        break;
      default:
        control.setValue(false, { emitEvent: false });
        control.disable({ emitEvent: false });
        break;
    }
  }

  public get canSubmit(): boolean {
    if (!this.form) {
      return false;
    }

    const textAreasFilled = (
      ['startDoing', 'keepDoing', 'stopDoing'] as FeedbackTextareaField[]
    ).some(field => this.hasContent(field));

    const isChecking = Object.values(this.checkingField).some(isFieldChecking => isFieldChecking);

    const metaValid = !!(
      this.form.get('employeeId')?.valid &&
      this.form.get('competenceId')?.valid &&
      this.form.get('competenceTitle')?.valid
    );

    return textAreasFilled && !isChecking && metaValid;
  }

  private hasContent(field: FeedbackTextareaField): boolean {
    const value = this.form?.get(field)?.value;
    return typeof value === 'string' ? value.trim().length > 0 : !!value;
  }
}

type FeedbackTextareaField = 'startDoing' | 'keepDoing' | 'stopDoing';

const nonWhitespaceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (typeof value === 'string' && value.trim().length > 0) {
    return null;
  }

  return { whitespace: true };
};
